import React, { useCallback, useEffect, useId } from 'react';
import { FieldError } from 'react-hook-form';
import { t } from '@lingui/macro';
import z from 'zod';

import BusinessType, {
  ALL_BUSINESS_TYPES,
} from '@rover/constants/src/payments/businessType.constants';
import { Box } from '@rover/kibble/core';
import { useKibbleForm } from '@rover/kibble/forms';
import RadioField from '@rover/kibble/forms/fields/RadioField';
import {
  RadioFieldOptionConfig,
  RadioFieldVariants,
} from '@rover/kibble/forms/fields/RadioField/RadioField.common';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import useIsBHUser from '@rover/shared/js/hooks/useIsBHUser';
import { SearchPageBoardingTypeFilterSelect } from '@rover/shared/js/search/utilities/analyticsEvents';

type Props = {
  businessType?: BusinessType | BusinessType[];
  required?: boolean;
  error?: boolean;
  defaultSetting?: boolean;
  onChange: (arg0: BusinessType | BusinessType[]) => void;
  layoutVariant?: RadioFieldVariants;
  placement?: string;
};

export const allBusinessTypesValue = ALL_BUSINESS_TYPES.join(',');

export default function BoardingTypeFilter({
  businessType,
  error = false,
  placement = undefined,
  onChange,
  layoutVariant = 'default',
}: Props): JSX.Element {
  const { isLoading } = useCurrentUser();
  const isBrightHorizonsUser = useIsBHUser();

  // because we have multiple instances of this component on the page, we need to generate a unique schema key for each
  // otherwise react hook form will only update the last rendered instance
  const boardingTypeFilterId = useId();
  const uniqueSchemaKey = `boardingTypeFilter-${boardingTypeFilterId}`;
  const schema = z.object({
    [uniqueSchemaKey]: z.optional(
      z.enum([allBusinessTypesValue, BusinessType.SITTER, BusinessType.FACILITY])
    ),
  });

  const getDefaultRadioValue = (): string => {
    if (businessType !== undefined) {
      return Array.isArray(businessType) ? businessType.join(',') : businessType;
    }
    return isBrightHorizonsUser ? BusinessType.SITTER : allBusinessTypesValue;
  };

  const { configureField, reset } = useKibbleForm({
    schema,
    defaultValues: {
      [uniqueSchemaKey]: getDefaultRadioValue(),
    },
  });

  useEffect(() => {
    // Set default when user data loads and no business type is selected
    if (businessType === undefined && !isLoading) {
      const defaultValue = isBrightHorizonsUser ? BusinessType.SITTER : ALL_BUSINESS_TYPES;
      const defaultRadioValue = isBrightHorizonsUser ? BusinessType.SITTER : allBusinessTypesValue;

      onChange(defaultValue);
      reset({ [uniqueSchemaKey]: defaultRadioValue });
    }
  }, [isLoading]);

  const handleChange = useCallback<(changeEvent: { value: string | number }) => void>(
    ({ value }) => {
      if (typeof value !== 'string') return; // this is just a type refinement it should never happen since we only pass strings

      // update parent state
      switch (value) {
        case BusinessType.SITTER:
          onChange(BusinessType.SITTER);
          break;
        case BusinessType.FACILITY:
          onChange(BusinessType.FACILITY);
          break;
        case allBusinessTypesValue:
          onChange(ALL_BUSINESS_TYPES);
          break;
        default:
          onChange(ALL_BUSINESS_TYPES);
          break;
      }

      emitAnalyticsEvent(
        new SearchPageBoardingTypeFilterSelect({
          boardingType: value,
          placement,
        })
      );
    },
    [onChange, placement]
  );

  const errorMessage: FieldError = {
    type: 'required',
    message: 'Please choose a boarding option', // translation for this is in the fieldErrorTranslationMap
  };

  const radioOptions: RadioFieldOptionConfig[] = [
    {
      value: BusinessType.SITTER,
      primaryLabel: t`In a sitter's home`,
    },
    {
      value: BusinessType.FACILITY,
      primaryLabel: t`At a local facility`,
    },
    {
      value: allBusinessTypesValue,
      primaryLabel: t`All types`,
    },
  ];

  return (
    <Box m="0x" p="0x" border="none" data-testid="boarding-type-filter">
      <RadioField
        {...configureField(uniqueSchemaKey)}
        name="boarding-type"
        primaryLabel={t`Boarding types:`}
        options={radioOptions}
        onChange={handleChange}
        fieldError={error ? errorMessage : undefined}
        variant={layoutVariant}
        primaryLabelSize="100"
        primaryLabelVariant="compact"
      />
    </Box>
  );
}
